import * as React from "react";
import {
    Button,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Tabs,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useIsMutating } from "@tanstack/react-query";
import { useFetchInsuranceProviders } from "../../../../hooks/queries/client";
import { ClaimStatus, ClaimsHasRemit } from "../../types/claims.types";
import {
    convertDateFilterStringToDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import {
    useGetClaimsListsStats,
    useFetchRejectionReasons,
} from "../../hooks/claims.queries";
import { IAppCustomDates } from "../../../../utils/types";
import { useDebounce } from "../../../../hooks/helpers";
import CreateManualClaimForm from "../CreateManualClaimForm/CreateManualClaimForm";
import { Source } from "../../../notes/types/notes.types";
import ReadyForSubmissionList from "./ReadyForSubmissionList/ReadyForSubmissionList";
import OnHoldClaimsList from "./OnHoldClaimsList/OnHoldClaimsList";
import AwaitingResponseList from "./AwaitingResponseList/AwaitingResponseList";
import RemitReceivedlClaimsList from "./RemitReceivedClaimsList/RemitReceivedClaimsList";
import RequiresActionList from "./RequiresActionList/RequiresActionList";
import ClosedClaimsList from "./ClosedClaimsList/ClosedClaimsList";
import Loader from "../../../../shared-ui/Loader/Loader";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import InsuranceProviderFilter from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import { getClaimStatusFilterLabel } from "../../helpers/claims.helper";
import { useFetchNormalizedEmployerNames } from "../../../clients/hooks/clients.queries";
import { CPT_CODES } from "../../../../utils/constants";

export default function ClaimsLists() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showDialog, setShowDialog] = React.useState(false);

    const tabInUrl = searchParams.get("tab");

    const handleSetTabInUrl = (tab: string) => {
        searchParams.set("tab", tab);
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const searchString = searchParams.get("search") || "";

    const searchStringFilter = useDebounce(searchString, 500);

    const fromDateFilter = searchParams.get("note_dos_from");
    const toDateFilter = searchParams.get("note_dos_to");

    const insuranceProvidersFilter = searchParams.getAll(
        "insurance_providers[]"
    );
    const cptCodesFilter = searchParams.getAll("cpt_codes[]");
    const employerNamesFilter = searchParams.getAll("employer_names[]");
    const claimsStatusFilter = searchParams.get("status");
    const hasRemitStatusFilter = searchParams.get("has_remits") || "";
    const customDateFilter =
        (searchParams.get("date_range") as IAppCustomDates) ||
        IAppCustomDates.LAST_90_DAYS;

    const getHasRemitFilter = () => {
        if (hasRemitStatusFilter === ClaimsHasRemit.SHOW_CLAIMS_WITH_REMIT) {
            return "true";
        }
        if (hasRemitStatusFilter === ClaimsHasRemit.HIDE_CLAIMS_WITH_REMIT) {
            return "false";
        }
        return "";
    };

    const claimSourceFilter = searchParams.get("source") || "";
    const showVoidedFilter = searchParams.get("show_voided") || "false";
    const rejectionReasonsFilter = searchParams.get("rejection_reasons") || "";
    const showArchivedNotesFilter =
        searchParams.get("show_archived_notes") || "";

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const { data: claimsStats, isLoading: cliamsStatsLoading } =
        useGetClaimsListsStats({
            claim_statuses: claimsStatusFilter
                ? [claimsStatusFilter]
                : undefined,
            custom_date: customDateFilter,
            search_string: searchStringFilter,
            note_dos_from: fromDateFilter,
            has_remits: getHasRemitFilter(),
            note_dos_to: toDateFilter,
            insurance_provider_ids: insuranceProvidersFilter,
            cpt_codes: cptCodesFilter,
            employer_names: employerNamesFilter,
            page: 1,
            limit: 20,
            claim_source: claimSourceFilter,
            show_voided: showVoidedFilter,
            status_note_categories: rejectionReasonsFilter
                ? [rejectionReasonsFilter]
                : undefined,
            show_old_claims: Boolean(searchStringFilter),
            show_archived_notes: showArchivedNotesFilter,
        });

    const insuranceProviders = useFetchInsuranceProviders();
    const rejectionReasons = useFetchRejectionReasons();
    const employerNames = useFetchNormalizedEmployerNames(undefined, true);

    const tabItems = [
        {
            key: "submission_ready",
            label: "Ready for submission",
            children: (
                <ReadyForSubmissionList searchString={searchStringFilter} />
            ),
            onClick: () => handleSetTabInUrl("submission_ready"),
            count: cliamsStatsLoading ? (
                <Loader loaderType="inline" className="size-2" />
            ) : (
                claimsStats?.data.ready_for_submission_count
            ),
        },
        {
            key: "on_hold",
            label: "On Hold",
            children: <OnHoldClaimsList searchString={searchStringFilter} />,
            onClick: () => handleSetTabInUrl("on_hold"),
            count: cliamsStatsLoading ? (
                <Loader loaderType="inline" className="size-2" />
            ) : (
                claimsStats?.data.on_hold_count
            ),
        },
        {
            key: "awaiting_response",
            label: "Awaiting response",
            children: (
                <AwaitingResponseList searchString={searchStringFilter} />
            ),
            onClick: () => handleSetTabInUrl("awaiting_response"),
            count: cliamsStatsLoading ? (
                <Loader loaderType="inline" className="size-2" />
            ) : (
                claimsStats?.data.awaiting_response_count
            ),
        },
        {
            key: "remit_received",
            label: "Remit received",
            children: (
                <RemitReceivedlClaimsList searchString={searchStringFilter} />
            ),
            onClick: () => handleSetTabInUrl("remit_received"),
            count: cliamsStatsLoading ? (
                <Loader loaderType="inline" className="size-2" />
            ) : (
                claimsStats?.data.remit_received_count
            ),
        },
        {
            key: "requires_action",
            label: "Requires action",
            children: <RequiresActionList searchString={searchStringFilter} />,
            onClick: () => handleSetTabInUrl("requires_action"),
            count: cliamsStatsLoading ? (
                <Loader loaderType="inline" className="size-2" />
            ) : (
                claimsStats?.data.requires_action_count
            ),
        },
        {
            key: "closed",
            label: "Closed",
            children: <ClosedClaimsList searchString={searchStringFilter} />,
            onClick: () => handleSetTabInUrl("closed"),
            count: cliamsStatsLoading ? (
                <Loader loaderType="inline" className="size-2" />
            ) : (
                claimsStats?.data.closed_count
            ),
        },
    ];

    const filteredInsuranceProviderNames = insuranceProviders?.data?.data
        ?.filter((prov) =>
            insuranceProvidersFilter.includes(prov.insurance_provider_id)
        )
        .map((prov) => prov.alias || prov.name);

    const statusOptionsForSelect = [
        ...Object.values(ClaimStatus).map((status) => {
            return status;
        }),
    ];

    const customDateForSelect = [
        ...Object.values(IAppCustomDates).map((item) => {
            return item;
        }),
    ];

    const clearColumnFilters = () => {
        const paramsToDelete = Array.from(searchParams.keys()).filter((key) =>
            key.endsWith("_col_filter")
        );

        if (paramsToDelete.length > 0) {
            paramsToDelete.forEach((param) => searchParams.delete(param));
        }
    };

    // onChange handler for search input
    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        clearColumnFilters();
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        clearColumnFilters();
        if (date === null) searchParams.delete("note_dos_from");
        else searchParams.set("note_dos_from", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        clearColumnFilters();
        if (date === null) searchParams.delete("note_dos_to");
        else searchParams.set("note_dos_to", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleStatusChange = (value: string) => {
        clearColumnFilters();
        if (value === "") searchParams.delete("status");
        else searchParams.set("status", value);
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleHasRemitChange = (value: string) => {
        clearColumnFilters();
        if (value === "") searchParams.delete("has_remits");
        else searchParams.set("has_remits", value);
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };
    const handleCustomDateChange = (value: IAppCustomDates) => {
        clearColumnFilters();
        searchParams.set("date_range", value);
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleClaimSourceChange = (value: string) => {
        clearColumnFilters();
        if (value === "") searchParams.delete("source");
        else searchParams.set("source", value);
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleShowVoidedFilterChange = (status: string) => {
        clearColumnFilters();
        searchParams.set("show_voided", status);
        setSearchParams(searchParams);
    };

    const handleRejectionReasonsChange = (value: string) => {
        clearColumnFilters();
        if (value === "") searchParams.delete("rejection_reasons");
        else searchParams.set("rejection_reasons", value);
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        clearColumnFilters();
        searchParams.delete("insurance_providers[]");
        insuranceProviderIds.forEach((insuranceProviderId) =>
            searchParams.append("insurance_providers[]", insuranceProviderId)
        );
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetCptCodesFilter = (cptCode: string) => {
        clearColumnFilters();
        if (cptCodesFilter.includes(cptCode)) {
            searchParams.delete("cpt_codes[]", cptCode);
        } else {
            searchParams.append("cpt_codes[]", cptCode);
        }
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetEmployerNamesFilter = (employerName: string) => {
        clearColumnFilters();
        if (employerNamesFilter.includes(employerName)) {
            searchParams.delete("employer_names[]", employerName);
        } else {
            searchParams.append("employer_names[]", employerName);
        }
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const getCptCodesFilterToDisplay = () => {
        if (cptCodesFilter.length > 1) {
            return `CPT code (${cptCodesFilter.length})`;
        }
        return cptCodesFilter[0]
            ? removeEnumUnderscore(cptCodesFilter[0])
            : "CPT code";
    };

    const getEmployerNamesFilterToDisplay = () => {
        if (employerNamesFilter.length > 1) {
            return `Employer name (${employerNamesFilter.length})`;
        }
        return employerNamesFilter[0]
            ? truncateString(employerNamesFilter[0])
            : "Employer name";
    };

    const getArchivedNotesFilterToDisplay = () => {
        if (showArchivedNotesFilter === "true") {
            return "Archived";
        }
        if (showArchivedNotesFilter === "false") {
            return "Unarchived";
        }

        return "Archive status";
    };

    const handleSetArchivedNotesFilter = (archived: string) => {
        clearColumnFilters();
        if (archived === "") searchParams.delete("show_archived_notes");
        else searchParams.set("show_archived_notes", archived);
        searchParams.set("page", "1");
        localStorage.setItem("claimslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const getVoidedClaimFilterToDisplay = () => {
        if (showVoidedFilter === "true") {
            return "Voided claims";
        }
        return "Unvoided claims";
    };

    const createClaimIsMutating = useIsMutating(["create-manual-claim"]);

    return (
        <>
            <Dialog open={showDialog}>
                <DialogContent
                    variant="center"
                    title="Create manual claim"
                    saveText="Create claim"
                    handleCloseDialog={() => setShowDialog(false)}
                    submitBtnFormValue="create-claim"
                    isSubmitBtnDisabled={createClaimIsMutating > 0}
                    isCancelBtnDisabled={createClaimIsMutating > 0}
                >
                    <CreateManualClaimForm
                        onFormSubmit={() => setShowDialog(false)}
                    />
                </DialogContent>
            </Dialog>
            <Card classNames="flex flex-col gap-y-[--spacing-items] mb-[--spacing-header]">
                <div>
                    <SearchInput
                        containerClass="w-[400px]"
                        placeholder="Search by client, claim no or clinician"
                        onChange={handleSetSearchString}
                        defaultValue={searchString}
                    />
                </div>
                <div className="flex gap-x-[--spacing-items] gap-y-[--spacing-items] flex-wrap justify-between">
                    <div className="flex gap-[--spacing-items] flex-wrap items-center ">
                        <InsuranceProviderFilter
                            values={insuranceProvidersFilter}
                            onChange={handleInsuranceProviderIdsChange}
                            showSelfPay={false}
                        />
                        <Dropdown>
                            <DropdownTrigger
                                asChild
                                disabled={
                                    Boolean(employerNames.error) ||
                                    employerNames.isLoading
                                }
                            >
                                <FilterButton>
                                    {employerNames.isLoading && "Loading.."}
                                    {employerNames.error &&
                                        "Error loading employer names"}
                                    {employerNames?.data?.data &&
                                        getEmployerNamesFilterToDisplay()}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent
                                align="start"
                                width="auto"
                                maxHeight={216}
                            >
                                {employerNames?.data?.data?.map((employer) => (
                                    <DropdownItem
                                        key={employer}
                                        onClick={() =>
                                            handleSetEmployerNamesFilter(
                                                employer
                                            )
                                        }
                                        isMulti
                                        itemValue={employer}
                                        values={employerNamesFilter}
                                    >
                                        {employer}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="normal-case">
                                    {getCptCodesFilterToDisplay()}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent
                                align="start"
                                width="auto"
                                maxHeight={216}
                            >
                                {CPT_CODES.map((code) => (
                                    <DropdownItem
                                        key={code}
                                        onClick={() =>
                                            handleSetCptCodesFilter(code)
                                        }
                                        isMulti
                                        itemValue={code}
                                        values={cptCodesFilter}
                                    >
                                        {removeEnumUnderscore(code)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="capitalize">
                                    {claimsStatusFilter
                                        ? truncateString(
                                              removeEnumUnderscore(
                                                  claimsStatusFilter
                                              )
                                          )
                                        : "Status"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="start" width="auto">
                                <DropdownItem
                                    isSelected={!claimsStatusFilter}
                                    onClick={() => handleStatusChange("")}
                                >
                                    All
                                </DropdownItem>
                                {statusOptionsForSelect.map((status) => (
                                    <DropdownItem
                                        isSelected={
                                            claimsStatusFilter === status
                                        }
                                        key={status}
                                        onClick={() =>
                                            handleStatusChange(status)
                                        }
                                    >
                                        {getClaimStatusFilterLabel(status)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="capitalize">
                                    {customDateFilter
                                        ? truncateString(
                                              removeEnumUnderscore(
                                                  customDateFilter
                                              )
                                          )
                                        : "Date range"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="start" width={200}>
                                {customDateForSelect.map((range) => (
                                    <DropdownItem
                                        isSelected={customDateFilter === range}
                                        key={range}
                                        onClick={() =>
                                            handleCustomDateChange(range)
                                        }
                                    >
                                        {removeEnumUnderscore(range)}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                        <Popover>
                            <PopoverTrigger asChild>
                                <FilterButton>Date</FilterButton>
                            </PopoverTrigger>
                            <PopoverContent align="end">
                                <div className="mb-1">
                                    <DatePicker
                                        label="From"
                                        placeholderText="MM/DD/YYYY"
                                        selected={fromDateFilterToDate}
                                        onChange={handleFromDateFilterChange}
                                        maxDate={
                                            toDateFilterToDate ||
                                            new Date(Date.now())
                                        }
                                        isClearable
                                    />
                                </div>
                                <DatePicker
                                    label="To"
                                    placeholderText="MM/DD/YYYY"
                                    selected={toDateFilterToDate}
                                    onChange={handleToDateFilterChange}
                                    minDate={fromDateFilterToDate}
                                    isClearable
                                />
                            </PopoverContent>
                        </Popover>
                        {/* <Button>Export</Button> */}
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="capitalize">
                                    {hasRemitStatusFilter
                                        ? truncateString(
                                              removeEnumUnderscore(
                                                  hasRemitStatusFilter
                                              )
                                          )
                                        : "Has Remit"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="start" width={230}>
                                <DropdownItem
                                    isSelected={!hasRemitStatusFilter}
                                    onClick={() => handleHasRemitChange("")}
                                >
                                    Show All Claims
                                </DropdownItem>
                                <DropdownItem
                                    isSelected={
                                        hasRemitStatusFilter ===
                                        ClaimsHasRemit.SHOW_CLAIMS_WITH_REMIT
                                    }
                                    onClick={() =>
                                        handleHasRemitChange(
                                            ClaimsHasRemit.SHOW_CLAIMS_WITH_REMIT
                                        )
                                    }
                                >
                                    {makeStringFirstLetterCapital(
                                        removeEnumUnderscore(
                                            ClaimsHasRemit.SHOW_CLAIMS_WITH_REMIT
                                        )
                                    )}
                                </DropdownItem>
                                <DropdownItem
                                    isSelected={
                                        hasRemitStatusFilter ===
                                        ClaimsHasRemit.HIDE_CLAIMS_WITH_REMIT
                                    }
                                    onClick={() =>
                                        handleHasRemitChange(
                                            ClaimsHasRemit.HIDE_CLAIMS_WITH_REMIT
                                        )
                                    }
                                >
                                    {makeStringFirstLetterCapital(
                                        removeEnumUnderscore(
                                            ClaimsHasRemit.HIDE_CLAIMS_WITH_REMIT
                                        )
                                    )}
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="capitalize">
                                    {claimSourceFilter
                                        ? truncateString(claimSourceFilter)
                                        : "Source"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="start" width="auto">
                                <DropdownItem
                                    isSelected={!claimSourceFilter}
                                    onClick={() => handleClaimSourceChange("")}
                                >
                                    Show All Claims
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleClaimSourceChange(Source.MANUAL)
                                    }
                                    isSelected={
                                        claimSourceFilter === Source.MANUAL
                                    }
                                >
                                    {Source.MANUAL}
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleClaimSourceChange(
                                            Source.ELECTRONIC
                                        )
                                    }
                                    isSelected={
                                        claimSourceFilter === Source.ELECTRONIC
                                    }
                                >
                                    {Source.ELECTRONIC}
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="capitalize">
                                    {getVoidedClaimFilterToDisplay()}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width={200}>
                                <DropdownItem
                                    isSelected={showVoidedFilter === "true"}
                                    onClick={() =>
                                        handleShowVoidedFilterChange("true")
                                    }
                                >
                                    Voided claims
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleShowVoidedFilterChange("false")
                                    }
                                    isSelected={showVoidedFilter === "false"}
                                >
                                    Unvoided claims
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger
                                asChild
                                disabled={
                                    Boolean(rejectionReasons.error) ||
                                    rejectionReasons.isLoading
                                }
                            >
                                <FilterButton className="capitalize">
                                    {rejectionReasons.isLoading && "Loading.."}
                                    {rejectionReasons.error &&
                                        "Error loading reasons"}
                                    {rejectionReasons?.data?.data &&
                                        (rejectionReasonsFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      rejectionReasonsFilter
                                                  )
                                              )
                                            : "Rejection Reason")}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="start" width={230}>
                                <DropdownItem
                                    isSelected={!rejectionReasonsFilter}
                                    onClick={() =>
                                        handleRejectionReasonsChange("")
                                    }
                                >
                                    All
                                </DropdownItem>
                                {rejectionReasons?.data?.data
                                    ?.sort((a, b) => a.localeCompare(b))
                                    .map((reason) => (
                                        <DropdownItem
                                            isSelected={
                                                rejectionReasonsFilter ===
                                                reason
                                            }
                                            key={reason}
                                            onClick={() =>
                                                handleRejectionReasonsChange(
                                                    reason
                                                )
                                            }
                                        >
                                            {removeEnumUnderscore(reason)}
                                        </DropdownItem>
                                    ))}
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="normal-case">
                                    {getArchivedNotesFilterToDisplay()}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width={170}>
                                <DropdownItem
                                    isSelected={!showArchivedNotesFilter}
                                    onClick={() =>
                                        handleSetArchivedNotesFilter("")
                                    }
                                >
                                    Show all
                                </DropdownItem>
                                <DropdownItem
                                    isSelected={
                                        showArchivedNotesFilter === "true"
                                    }
                                    onClick={() =>
                                        handleSetArchivedNotesFilter("true")
                                    }
                                >
                                    Yes
                                </DropdownItem>
                                <DropdownItem
                                    isSelected={
                                        showArchivedNotesFilter === "false"
                                    }
                                    onClick={() =>
                                        handleSetArchivedNotesFilter("false")
                                    }
                                >
                                    No
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                    </div>
                    <Button onClick={() => setShowDialog(true)}>
                        Create manual claim
                    </Button>
                </div>
                <TableFiltersDisplay
                    appliedFilters={[
                        {
                            key: "insurance_providers[]",
                            values: filteredInsuranceProviderNames || [],
                        },
                        {
                            key: "employer_names[]",
                            label: "Employer name",
                            values: employerNamesFilter,
                            preserveValues: true,
                        },
                        {
                            key: "cpt_codes[]",
                            label: "CPT code",
                            values: cptCodesFilter,
                        },
                        {
                            key: "status",
                            values: claimsStatusFilter || "",
                        },
                        {
                            key: "date_range",
                            values:
                                customDateFilter !== IAppCustomDates.ALL_TIME &&
                                !(fromDateFilter && toDateFilter)
                                    ? customDateFilter
                                    : "",
                            clearValue: IAppCustomDates.ALL_TIME,
                        },
                        {
                            key: "note_dos_from",
                            label: "From date",
                            values: fromDateFilter || "",
                        },
                        {
                            key: "note_dos_to",
                            label: "To date",
                            values: toDateFilter || "",
                        },
                        {
                            key: "has_remits",
                            values: hasRemitStatusFilter,
                        },
                        {
                            key: "source",
                            values: claimSourceFilter,
                        },
                        {
                            key: "show_voided",
                            values:
                                showVoidedFilter === "true"
                                    ? "Voided claims"
                                    : "",
                        },
                        {
                            key: "rejection_reasons",
                            label: "Rejection reason",
                            values: rejectionReasonsFilter,
                        },
                        {
                            key: "show_archived_notes",
                            label: "Archive status",
                            values: showArchivedNotesFilter
                                ? getArchivedNotesFilterToDisplay()
                                : "",
                        },
                    ]}
                    wrapperClass="mt-[--spacing-items]"
                    localStorageKey="claimslist_filters"
                />
            </Card>
            <Tabs
                items={tabItems}
                size="medium"
                className="w-full rounded-b-none"
                defaultActiveKey={tabInUrl || tabItems[0].key}
            />
        </>
    );
}
